import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import RemindersList from '../components/RemindersList';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Bell, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RemindersPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [scope, setScope] = useState('due');
  const [reload, setReload] = useState(0);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!title.trim()) { toast.error('Enter a reminder title'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/dashboard/reminders`, {
        title: title.trim(),
        dueAt: due ? new Date(due).toISOString() : null,
        reminderType: 'DATETIME',
      }, { headers });
      toast.success('Reminder created');
      setTitle(''); setDue(''); setOpen(false);
      setReload((r) => r + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create reminder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="reminders-title">
            <Bell className="h-6 w-6 text-primary" /> Reminders
          </h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-reminder-btn"><Plus className="h-4 w-4 mr-1" /> New reminder</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="new-reminder-dialog">
              <DialogHeader>
                <DialogTitle>New reminder</DialogTitle>
                <DialogDescription>A simple nudge for yourself. Leave the date blank for an anytime to-do.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>What to remember</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Re-shoe Cash next visit" data-testid="reminder-title-field" />
                </div>
                <div>
                  <Label>When (optional)</Label>
                  <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)}
                    data-testid="reminder-due-field" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} disabled={saving} data-testid="save-reminder-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant={scope === 'due' ? 'default' : 'outline'}
            onClick={() => setScope('due')} data-testid="scope-due">Due now</Button>
          <Button size="sm" variant={scope === 'all' ? 'default' : 'outline'}
            onClick={() => setScope('all')} data-testid="scope-all">All active</Button>
        </div>

        <RemindersList scope={scope} reloadSignal={reload} onChanged={() => setReload((r) => r + 1)} />
      </div>
    </DashboardLayout>
  );
}
